import React, { useEffect, useCallback, useState } from 'react';
import { Box, Button } from '@mui/material';

import { Container } from '@/src/ui';
import { apiRequest } from '@/src/services';
import AttemptDetailsFilters from './components/AttemptDetailsFilters';
import type { FilterState } from './models/filterDTOs';
import type { StudentExamAttemptsDTO } from './models/attemptDetailsDTOs';
import AttemptDetailsTable from './components/AttemptDetailsTable';
import { useAuth } from '@/src/app/context/useAuth';

const AttemptDetailsPage: React.FC = () => {
    document.title = 'Attempt Details';

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState<Array<StudentExamAttemptsDTO>>([]);
    const [hasSubmittedFilters, setHasSubmittedFilters] = useState(false);
    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const [filterState, setFilterState] = useState<FilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    classId: parsed.classId || 0,
                    examId:
                        parsed.examId ||
                        (parsed.examIds && parsed.examIds.length > 0 ? parsed.examIds[0] : 0),
                    studentId: 0,
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
            classId: 0,
            examId: 0,
            studentId: 0,
        };
    });

    const refreshTableData = useCallback(
        async (filters: FilterState = filterState) => {
            setHasSubmittedFilters(true);
            setError('');
            setLoaded(false);

            let requestUrl = `assessments/studentExamAttempts/attemptDetails?`;
            requestUrl += `districtId=${filters.districtId}`;
            requestUrl += `&schoolId=${filters.schoolId}`;
            requestUrl += `&classId=${filters.classId}`;
            requestUrl += `&gradeLevelMasterId=${filters.gradeLevelMasterId}`;
            requestUrl += `&examId=${filters.examId}`;
            requestUrl += `&studentId=${filters.studentId}`;

            try {
                const res = await apiRequest<StudentExamAttemptsDTO[]>('GET', requestUrl);
                setData(res.data);
            } catch {
                setError('Failed to fetch exam summary data');
            } finally {
                setLoaded(true);
            }
        },
        [filterState]
    );

    useEffect(() => {
        if (!userId) return;

        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            const existingFilters = saved ? JSON.parse(saved) : {};

            const universalFilters = {
                ...existingFilters,
                gradeLevelMasterId: filterState.gradeLevelMasterId,
                districtId: filterState.districtId,
                schoolId: filterState.schoolId,
                classId: filterState.classId,
                examId: filterState.examId,
                examIds: [filterState.examId],
            };

            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, userId, UNIVERSAL_FILTER_KEY]);

    useEffect(() => {
        const fs = filterState;
        if (fs.districtId && fs.schoolId && fs.gradeLevelMasterId && fs.classId && fs.examId) {
            refreshTableData(fs);
        } else {
            setLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container>
            <h1 className="page-header"> Attempt Details </h1>
            <br />

            <AttemptDetailsFilters
                refreshTableData={refreshTableData}
                filterState={filterState}
                setFilterState={setFilterState}
            />

            <Box sx={{ pb: 2, display: 'flex', justifyContent: 'start' }}>
                {filterState.examId !== null && filterState.examId !== 0 && (
                    <div className="ml-auto">
                        <Button
                            onClick={() => null}
                            sx={{
                                textTransform: 'none',
                            }}
                            variant="outlined"
                            href={`/reports/questionList/${filterState.examId}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            View Assessment
                        </Button>
                    </div>
                )}
            </Box>

            {hasSubmittedFilters && (
                <AttemptDetailsTable
                    data={data}
                    isLoading={!loaded}
                    error={error}
                    refreshTableData={refreshTableData}
                />
            )}
        </Container>
    );
};

export default AttemptDetailsPage;
